// Dynamic Token Service - Fetches real-time token data from multiple sources
// Uses DexScreener API for prices and MonadVision for token pages

export interface DynamicToken {
    address: `0x${string}`;
    symbol: string;
    name: string;
    decimals: number;
    logoUrl?: string;
    price?: number;
    priceChange24h?: number;
    volume24h?: number;
    liquidity?: number;
    fdv?: number;
    marketCap?: number;
    monadvisionUrl: string;
}

// Format large numbers for display (e.g., 1.5M, 2.3K)
export function formatLargeNumber(num: number): string {
    if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(2)}B`;
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(2)}K`;
    return num.toFixed(2);
}

// Format price with appropriate decimal places
export function formatPrice(price: number): string {
    if (price >= 1) return price.toFixed(2);
    if (price >= 0.0001) return price.toFixed(6);
    if (price >= 0.0000001) return price.toFixed(10);
    return price.toExponential(4);
}

// Format percentage change
export function formatPercentChange(change: number): string {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(2)}%`;
}

// Core tokens with verified addresses on Monad
const CORE_TOKENS: Omit<DynamicToken, 'price' | 'priceChange24h' | 'volume24h' | 'liquidity' | 'fdv' | 'marketCap' | 'monadvisionUrl'>[] = [
    {
        address: '0x0862F464c8457266b66c58F1D7C1137B72647777' as `0x${string}`,
        symbol: 'DUCK',
        name: 'Duck on Monad',
        decimals: 18,
        logoUrl: TOKEN_IMAGE_URL
    },
    {
        address: '0x760AfE86e5de5fa0Ee542fc7B7B713e1c5425701' as `0x${string}`,
        symbol: 'WMON',
        name: 'Wrapped Monad',
        decimals: 18,
        logoUrl: '/images/mon.svg'
    },
    {
        address: '0x0000000000000000000000000000000000000000' as `0x${string}`,
        symbol: 'MON',
        name: 'Monad',
        decimals: 18,
        logoUrl: '/images/mon.svg'
    }
];

// DexScreener API for token data
const DEXSCREENER_API = 'https://api.dexscreener.com/latest/dex';

// Fetch token data from DexScreener
export async function fetchTokenFromDexScreener(address: string): Promise<Partial<DynamicToken> | null> {
    try {
        const response = await fetch(`${DEXSCREENER_API}/tokens/${address}`);
        if (!response.ok) return null;

        const data = await response.json();
        if (!data.pairs || data.pairs.length === 0) return null;

        // Get the most liquid pair
        const pair = data.pairs.sort((a: any, b: any) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0))[0];

        const baseToken = pair.baseToken.address.toLowerCase() === address.toLowerCase()
            ? pair.baseToken
            : pair.quoteToken;

        return {
            symbol: baseToken.symbol,
            name: baseToken.name,
            logoUrl: pair.info?.imageUrl,
            price: parseFloat(pair.priceUsd || '0'),
            priceChange24h: pair.priceChange?.h24 || 0,
            volume24h: pair.volume?.h24 || 0,
            liquidity: pair.liquidity?.usd || 0,
            fdv: pair.fdv || 0,
            marketCap: pair.marketCap || 0
        };
    } catch (error) {
        console.error('DexScreener fetch error:', error);
        return null;
    }
}

// Get all dynamic tokens with real-time data
export async function getDynamicTokens(): Promise<DynamicToken[]> {
    const tokens: DynamicToken[] = [];

    for (const coreToken of CORE_TOKENS) {
        // Skip native MON token for DexScreener (no liquidity pool)
        if (coreToken.address === '0x0000000000000000000000000000000000000000') {
            tokens.push({
                ...coreToken,
                price: undefined,
                priceChange24h: undefined,
                volume24h: undefined,
                liquidity: undefined,
                fdv: undefined,
                marketCap: undefined,
                monadvisionUrl: 'https://monadvision.com'
            });
            continue;
        }

        const dexData = await fetchTokenFromDexScreener(coreToken.address);

        tokens.push({
            ...coreToken,
            logoUrl: dexData?.logoUrl || coreToken.logoUrl,
            price: dexData?.price,
            priceChange24h: dexData?.priceChange24h,
            volume24h: dexData?.volume24h,
            liquidity: dexData?.liquidity,
            fdv: dexData?.fdv,
            marketCap: dexData?.marketCap,
            monadvisionUrl: `https://monadvision.com/token/${coreToken.address}`
        });
    }

    return tokens;
}

// Fetch single token with real-time data
export async function fetchDynamicToken(address: string): Promise<DynamicToken | null> {
    const coreToken = CORE_TOKENS.find(t => t.address.toLowerCase() === address.toLowerCase());

    const dexData = await fetchTokenFromDexScreener(address);

    if (!dexData && !coreToken) return null;

    return {
        address: address as `0x${string}`,
        symbol: coreToken?.symbol || dexData?.symbol || 'UNKNOWN',
        name: coreToken?.name || dexData?.name || 'Unknown Token',
        decimals: coreToken?.decimals || 18,
        logoUrl: dexData?.logoUrl || coreToken?.logoUrl,
        price: dexData?.price,
        priceChange24h: dexData?.priceChange24h,
        volume24h: dexData?.volume24h,
        liquidity: dexData?.liquidity,
        fdv: dexData?.fdv,
        marketCap: dexData?.marketCap,
        monadvisionUrl: `https://monadvision.com/token/${address}`
    };
}

// Search for tokens on DexScreener
export async function searchTokens(query: string): Promise<DynamicToken[]> {
    try {
        const response = await fetch(`${DEXSCREENER_API}/search/?q=${encodeURIComponent(query)}`);
        if (!response.ok) return [];

        const data = await response.json();
        if (!data.pairs) return [];

        // Filter for Monad chain and deduplicate
        const monadPairs = data.pairs.filter((p: any) => p.chainId === 'monad');
        const seen = new Set<string>();
        const tokens: DynamicToken[] = [];

        for (const pair of monadPairs) {
            const address = pair.baseToken.address;
            if (seen.has(address.toLowerCase())) continue;
            seen.add(address.toLowerCase());

            tokens.push({
                address: address as `0x${string}`,
                symbol: pair.baseToken.symbol,
                name: pair.baseToken.name,
                decimals: 18,
                logoUrl: pair.info?.imageUrl,
                price: parseFloat(pair.priceUsd || '0'),
                priceChange24h: pair.priceChange?.h24 || 0,
                volume24h: pair.volume?.h24 || 0,
                liquidity: pair.liquidity?.usd || 0,
                fdv: pair.fdv || 0,
                marketCap: pair.marketCap || 0,
                monadvisionUrl: `https://monadvision.com/token/${address}`
            });
        }

        return tokens;
    } catch (error) {
        console.error('Token search error:', error);
        return [];
    }
}

// React hook for dynamic token data
import { useState, useEffect } from 'react';
import { TOKEN_IMAGE_URL } from '../config/constants';

export function useDynamicToken(address: string) {
    const [token, setToken] = useState<DynamicToken | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        let mounted = true;

        async function load() {
            setIsLoading(true);
            try {
                const data = await fetchDynamicToken(address);
                if (mounted) {
                    setToken(data);
                    setError(null);
                }
            } catch (e) {
                if (mounted) setError(e as Error);
            } finally {
                if (mounted) setIsLoading(false);
            }
        }

        load();

        // Refresh every 30 seconds
        const interval = setInterval(load, 30000);

        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, [address]);

    return { token, isLoading, error };
}

export function useDynamicTokens() {
    const [tokens, setTokens] = useState<DynamicToken[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        let mounted = true;

        async function load() {
            setIsLoading(true);
            try {
                const data = await getDynamicTokens();
                if (mounted) {
                    setTokens(data);
                    setError(null);
                }
            } catch (e) {
                if (mounted) setError(e as Error);
            } finally {
                if (mounted) setIsLoading(false);
            }
        }

        load();

        // Refresh every 30 seconds
        const interval = setInterval(load, 30000);

        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, []);

    return { tokens, isLoading, error };
}

export function useTokenSearch(query: string) {
    const [results, setResults] = useState<DynamicToken[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        if (!query || query.length < 2) {
            setResults([]);
            return;
        }

        let mounted = true;
        const debounce = setTimeout(async () => {
            setIsSearching(true);
            try {
                const tokens = await searchTokens(query);
                if (mounted) setResults(tokens);
            } catch (e) {
                console.error('Search error:', e);
            } finally {
                if (mounted) setIsSearching(false);
            }
        }, 300);

        return () => {
            mounted = false;
            clearTimeout(debounce);
        };
    }, [query]);

    return { results, isSearching };
}
